/**
 * <OptimizedImage> — site-wide image primitive.
 *
 * Goals:
 *  - Reduce LCP and bandwidth: emit responsive srcset and let the
 *    browser pick the smallest variant that fits the viewport.
 *  - Eliminate CLS: always reserve layout via aspect-ratio CSS or
 *    explicit width / height props.
 *  - Lazy by default; eager + fetchpriority="high" for hero images.
 *  - Use Unsplash's `auto=format` URL transform to negotiate AVIF /
 *    WebP per-browser without needing a manual <picture> element.
 *  - Use the backend image-proxy at /api/img for customer-assets URLs
 *    so those static photos also get responsive sizes and WebP.
 *  - Plain pass-through for everything else (data URLs, internal SVG,
 *    or assets already on a transforming CDN) so we never break.
 *
 * Usage:
 *   <OptimizedImage
 *     src="https://images.unsplash.com/photo-xyz?auto=format&fit=crop&w=2400&q=80"
 *     alt="Concierge guiding guests through the dunes"
 *     priority           // above-the-fold hero
 *     sizes="(min-width: 1280px) 100vw, 100vw"
 *     aspectRatio="16/9"
 *   />
 */
import React from "react";

// Responsive width breakpoints. Curated set covering the common mobile
// (480), tablet (768), laptop (1280) and desktop hero (1920) sizes.
// Browsers pick the smallest variant that satisfies `sizes` × DPR, so
// keeping the set lean (4 sources × 2 formats = 8 candidates per image
// instead of 16) reduces srcset parsing cost without harming visual
// quality — the 3:2 jumps between breakpoints are well below the
// threshold the eye notices on a single image.
const DEFAULT_WIDTHS = [480, 768, 1280, 1920];

// AVIF compresses ~25-35% better than WebP at the same perceptual
// quality, so we drive AVIF at a lower quality factor and leave WebP /
// JPEG at the higher one. q≈60 keeps AVIF visually lossless for
// photographic content while shaving roughly a third off the file size
// vs q=72 (measured: 105 KB → ~70 KB on the hero card images).
const AVIF_QUALITY_DELTA = -12;

const isUnsplash = (u) =>
  typeof u === "string" && u.includes("images.unsplash.com");

const isCustomerAssets = (u) =>
  typeof u === "string" && u.includes("customer-assets.emergentagent.com");

const isPexels = (u) =>
  typeof u === "string" && u.includes("images.pexels.com");

const isEmergentStatic = (u) =>
  typeof u === "string" && u.includes("static.prod-images.emergentagent.com");

const isCmsFile = (u) =>
  typeof u === "string" &&
  (u.startsWith("/api/files/") || u.includes("/api/files/"));

/**
 * Build a srcset for our own CMS-uploaded files. The /api/files/{path}
 * endpoint accepts `?w=` and `?fmt=` and returns transcoded WebP/AVIF
 * with year-long immutable cache headers (Pillow 12 native AVIF).
 */
const buildCmsFileSrcset = (url, widths, quality, format = "webp") => {
  const base = url.split("?")[0];
  return widths
    .map((w) => `${base}?w=${w}&q=${quality}&fmt=${format} ${w}w`)
    .join(", ");
};

const buildCmsFileSrc = (url, width, quality, format = "webp") => {
  const base = url.split("?")[0];
  return `${base}?w=${width}&q=${quality}&fmt=${format}`;
};

/**
 * Strip any existing `w=` / `q=` / `fm=` / `auto=` params from an
 * Unsplash URL so we can apply our own consistently. We keep `fit`,
 * `crop`, focal-point params, etc., untouched.
 */
const stripUnsplashSizing = (url) => {
  try {
    const u = new URL(url);
    ["w", "h", "q", "fm", "auto", "dpr"].forEach((p) =>
      u.searchParams.delete(p)
    );
    return u;
  } catch {
    return null;
  }
};

const buildUnsplashSrcset = (url, widths, quality, format) => {
  const u = stripUnsplashSizing(url);
  if (!u) return null;
  if (format === "avif" || format === "webp") {
    u.searchParams.set("fm", format);
  } else {
    u.searchParams.set("auto", "format");
  }
  u.searchParams.set("q", String(quality));
  return widths
    .map((w) => {
      const v = new URL(u.toString());
      v.searchParams.set("w", String(w));
      return `${v.toString()} ${w}w`;
    })
    .join(", ");
};

const buildUnsplashSrc = (url, width, quality) => {
  const u = stripUnsplashSizing(url);
  if (!u) return url;
  u.searchParams.set("auto", "format");
  u.searchParams.set("q", String(quality));
  u.searchParams.set("w", String(width));
  return u.toString();
};

/**
 * Customer-assets URLs go through the backend image proxy which
 * transcodes to WebP and resizes on the fly. The proxy responds with
 * year-long immutable cache headers so the CDN can absorb most traffic.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Bunny CDN base — when set, CMS-file URLs are rewritten to go through
// the Pull Zone instead of /api/files. Bunny Optimizer takes care of
// resize + AVIF/WebP transcoding at the edge, eliminating Pillow CPU
// on the origin pod. Leave empty to disable and fall back to the
// origin-side transform (current behaviour).
const CDN_BASE_URL = process.env.REACT_APP_CDN_BASE_URL || "";

const buildProxyUrl = (url, width, quality, format = "webp") =>
  `${BACKEND_URL}/api/img?url=${encodeURIComponent(url)}&w=${width}&q=${quality}&fmt=${format}`;

const buildProxySrcset = (url, widths, quality, format = "webp") =>
  widths.map((w) => `${buildProxyUrl(url, w, quality, format)} ${w}w`).join(", ");

// ── Bunny CDN URL builders ──────────────────────────────────────────────
// Pull-Zone rewrite: strip the origin host (if any), prepend CDN base,
// and translate our internal `?w=&fmt=&q=` syntax to Bunny Optimizer's
// `?width=&format=&quality=` syntax.
const toCdnPath = (url) => {
  if (!url) return "";
  if (url.startsWith("/")) return url.split("?")[0];
  try {
    return new URL(url).pathname;
  } catch {
    return url.split("?")[0];
  }
};

const buildCdnUrl = (url, width, quality, format = "webp") => {
  const path = toCdnPath(url);
  return `${CDN_BASE_URL}${path}?width=${width}&format=${format}&quality=${quality}`;
};

const buildCdnSrcset = (url, widths, quality, format = "webp") =>
  widths.map((w) => `${buildCdnUrl(url, w, quality, format)} ${w}w`).join(", ");

// Customer-assets / Pexels / emergent-static all live on external hosts.
// A Bunny Edge Rule rewrites a path prefix unique to each origin
// (e.g. `cdn.visitxaluca.com/job_*` → customer-assets,
//        `cdn.visitxaluca.com/pexels/*` → images.pexels.com) to fetch
// from that host. From the frontend's perspective we just need to
// translate the URL to the path the Edge Rule expects, prepend the CDN
// base, and let Bunny's Edge Rule do the origin switch transparently.
//
// Per-host prefix map:
//   • customer-assets.emergentagent.com — paths start with `/job_xxx/`
//     so they match the rule directly with no extra prefix.
//   • static.prod-images.emergentagent.com — same idea (covered by a
//     dedicated edge rule that strips the prefix it uses).
//   • images.pexels.com — paths look like `/photos/<id>/<file>` which
//     is too generic to match a unique edge rule, so we inject our own
//     `/pexels` prefix on the way out. The Bunny rule rewrites it back
//     to the bare Pexels path via `%{Path.1-}`.
const buildCdnExternalUrl = (url, width, quality, format = "webp") => {
  const path = toCdnPath(url);
  const prefixed = isPexels(url) ? `/pexels${path}` : path;
  return `${CDN_BASE_URL}${prefixed}?width=${width}&format=${format}&quality=${quality}`;
};

const buildCdnExternalSrcset = (url, widths, quality, format = "webp") =>
  widths.map((w) => `${buildCdnExternalUrl(url, w, quality, format)} ${w}w`).join(", ");

const OptimizedImage = React.forwardRef(function OptimizedImage(
  {
    src,
    alt = "",
    priority = false,
    sizes,
    widths = DEFAULT_WIDTHS,
    quality = 72,
    aspectRatio,
    width,
    height,
    className = "",
    style,
    onLoad,
    onError,
    objectPosition,
    objectFit = "cover",
    ...rest
  },
  ref
) {
  if (!src) return null;

  const lazy = !priority;
  const sharedAttrs = {
    alt,
    loading: lazy ? "lazy" : "eager",
    decoding: lazy ? "async" : "sync",
    fetchPriority: priority ? "high" : "low",
    width,
    height,
    onLoad,
    onError,
    ref,
    ...rest,
  };

  // Compose CLS-safe sizing styles. aspectRatio is the preferred path
  // for responsive containers (object-fit cover); explicit width/height
  // remain as a hint to the browser for reserve-space allocation.
  const composedStyle = {
    ...(aspectRatio ? { aspectRatio } : null),
    ...(objectFit ? { objectFit } : null),
    ...(objectPosition ? { objectPosition } : null),
    ...style,
  };

  // Per-format srcsets. Browsers pick the first <source> whose MIME
  // type they support, so AVIF > WebP > original fallback.
  let avifSrcset = null;
  let webpSrcset = null;
  let finalSrc = src;
  const defaultSizes =
    sizes ||
    (priority
      ? "100vw"
      : "(min-width: 1280px) 50vw, (min-width: 768px) 75vw, 100vw");

  if (isUnsplash(src)) {
    avifSrcset = buildUnsplashSrcset(src, widths, Math.max(35, quality + AVIF_QUALITY_DELTA), "avif");
    webpSrcset = buildUnsplashSrcset(src, widths, quality, "webp");
    finalSrc = buildUnsplashSrc(src, widths[widths.length - 1], quality);
  } else if (isCustomerAssets(src) || isPexels(src) || isEmergentStatic(src)) {
    if (CDN_BASE_URL) {
      // Route external hosts through Bunny — an Edge Rule matches the
      // host-specific path prefix (e.g. /job_* → customer-assets) and
      // swaps the origin at the edge. Pillow on the pod is never hit.
      avifSrcset = buildCdnExternalSrcset(src, widths, Math.max(35, quality + AVIF_QUALITY_DELTA), "avif");
      webpSrcset = buildCdnExternalSrcset(src, widths, quality, "webp");
      finalSrc = buildCdnExternalUrl(src, widths[widths.length - 1], quality, "webp");
    } else {
      // Fallback: backend /api/img Pillow proxy with whitelisted hosts.
      avifSrcset = buildProxySrcset(src, widths, Math.max(35, quality + AVIF_QUALITY_DELTA), "avif");
      webpSrcset = buildProxySrcset(src, widths, quality, "webp");
      finalSrc = buildProxyUrl(src, widths[widths.length - 1], quality, "webp");
    }
  } else if (isCmsFile(src)) {
    if (CDN_BASE_URL) {
      // Route through Bunny CDN + Optimizer. Bunny serves edge-cached
      // transformations, so Pillow CPU on the origin pod drops to zero
      // for image traffic (only the bare-original first fetch per file
      // ever hits us).
      avifSrcset = buildCdnSrcset(src, widths, Math.max(35, quality + AVIF_QUALITY_DELTA), "avif");
      webpSrcset = buildCdnSrcset(src, widths, quality, "webp");
      finalSrc = buildCdnUrl(src, widths[widths.length - 1], quality, "webp");
    } else {
      avifSrcset = buildCmsFileSrcset(src, widths, Math.max(35, quality + AVIF_QUALITY_DELTA), "avif");
      webpSrcset = buildCmsFileSrcset(src, widths, quality, "webp");
      finalSrc = buildCmsFileSrc(src, widths[widths.length - 1], quality, "webp");
    }
  }

  const hasAnySource = avifSrcset || webpSrcset;

  // When we have no source to emit (data URLs, internal SVG, etc.)
  // skip the <picture> wrapper to keep the DOM lean.
  if (!hasAnySource) {
    return (
      <img
        {...sharedAttrs}
        src={finalSrc}
        className={className}
        style={composedStyle}/>
    );
  }

  // The <img> inside <picture> intentionally has NO srcSet. Every
  // <source> above already covers responsive sizes for its MIME type,
  // and adding a srcSet on <img> would cause some browsers to
  // double-fetch (once for the <source> match, once for the <img>
  // candidate). The bare `src` is only used by browsers that do not
  // support <picture> at all (effectively none in modern usage).
  return (
    <picture>
      {avifSrcset && (
        <source type="image/avif" srcSet={avifSrcset} sizes={defaultSizes} />
      )}
      {webpSrcset && (
        <source type="image/webp" srcSet={webpSrcset} sizes={defaultSizes} />
      )}
      <img
        {...sharedAttrs}
        src={finalSrc}
        className={className}
        style={composedStyle}/>
    </picture>
  );
});

export default OptimizedImage;
export { isUnsplash, isCustomerAssets, buildUnsplashSrc, buildProxyUrl };
